import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import './Scorecard.css';

function Scorecard({ selectedMatches, nightMode }) {
  const scorecardRef = useRef(null);

  const renderScores = (match) => {
    const scores = [match.scoreA, match.scoreB];
    const maxScore = Math.max(...scores);
    return (
      <div className="scores">
        <span className={match.scoreA === maxScore ? 'winning-score' : 'losing-score'}>{match.scoreA}</span>
        <span className={match.scoreB === maxScore ? 'winning-score' : 'losing-score'}>{match.scoreB}</span>
      </div>
    );
  };

  const calculateTotalPointDifferential = () => {
    if (selectedMatches.length === 0) return 'No matches';
    
    let totalPointsA = 0;
    let totalPointsB = 0;
    
    // Count occurrences of team names to find the most common ones
    const teamANames = {};
    const teamBNames = {};
    
    selectedMatches.forEach(match => {
      totalPointsA += match.scoreA;
      totalPointsB += match.scoreB;
      
      teamANames[match.teamA] = (teamANames[match.teamA] || 0) + 1;
      teamBNames[match.teamB] = (teamBNames[match.teamB] || 0) + 1;
    });
    
    const difference = Math.abs(totalPointsA - totalPointsB);
    
    if (totalPointsA === totalPointsB) {
      return `Tied (${totalPointsA}-${totalPointsB})`;
    } else if (totalPointsA > totalPointsB) {
      // Get the most common Team A name
      const mostCommonTeamA = Object.keys(teamANames).reduce((a, b) => 
        teamANames[a] > teamANames[b] ? a : b
      );
      return `${mostCommonTeamA} +${difference}`;
    } else {
      // Get the most common Team B name
      const mostCommonTeamB = Object.keys(teamBNames).reduce((a, b) => 
        teamBNames[a] > teamBNames[b] ? a : b
      );
      return `${mostCommonTeamB} +${difference}`;
    }
  };

  const getDateRange = () => {
    if (selectedMatches.length === 0) return '';
    
    const dates = selectedMatches.map(match => new Date(match.date));
    const sortedDates = dates.sort((a, b) => a - b);
    const earliestDate = sortedDates[0];
    const latestDate = sortedDates[sortedDates.length - 1];
    
    if (selectedMatches.length === 1) {
      return earliestDate.toLocaleDateString();
    }
    
    // If same day, show just one date
    if (earliestDate.toDateString() === latestDate.toDateString()) {
      return earliestDate.toLocaleDateString();
    }
    
    return `${earliestDate.toLocaleDateString()} - ${latestDate.toLocaleDateString()}`;
  };

  const areDatesAllSame = () => {
    if (selectedMatches.length <= 1) return true;
    
    const firstDate = new Date(selectedMatches[0].date).toDateString();
    return selectedMatches.every(match => 
      new Date(match.date).toDateString() === firstDate
    );
  };

  const formatMatchDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const handleDownload = () => {
    if (scorecardRef.current) {
      html2canvas(scorecardRef.current).then((canvas) => {
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = 'volleyball-scorecard.png';
        link.click();
      });
    }
  };

  return (
    <div className={`scorecard-container ${nightMode ? 'night-mode' : ''}`}>
      <div className={`scorecard ${nightMode ? 'night-mode' : ''}`} ref={scorecardRef}>
        <div className="scorecard-header">
          <h2>Volleyball Final Scores</h2>
          <div className="scorecard-meta">
            <div className="meta-item">
              <span className="meta-label">Date:</span>
              <span className="meta-value">{getDateRange()}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Matches:</span>
              <span className="meta-value">{selectedMatches.length}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Total Point Differential:</span>
              <span className="meta-value">{calculateTotalPointDifferential()}</span>
            </div>
          </div>
        </div>
        <div className="scorecard-content">
          {selectedMatches.map((match, index) => (
            <div key={index} className="match-row">
              {!areDatesAllSame() && (
                <div className="match-header">
                  <span className="match-date">{formatMatchDate(match.date)}</span>
                  <span className="point-diff">+{Math.abs(match.scoreA - match.scoreB)}</span>
                </div>
              )}
              {areDatesAllSame() && (
                <div className="match-header-simple">
                  <span className="point-diff">+{Math.abs(match.scoreA - match.scoreB)}</span>
                </div>
              )}
              <div className="match-teams">
                <div className={`team team-a ${match.scoreA > match.scoreB ? 'winner' : 'loser'}`}>
                  <div className="team-name">{match.teamA}</div>
                </div>
                {renderScores(match)}
                <div className={`team team-b ${match.scoreB > match.scoreA ? 'winner' : 'loser'}`}>
                  <div className="team-name">{match.teamB}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <button className="download-button" onClick={handleDownload}>
        Download Scorecard
      </button>
    </div>
  );
}

export default Scorecard;